import { useCallback, useRef } from 'react';
import { IMapCoordinates, IMapGeoObject } from '@cian/frontend-newbuilding-map-component';
import { useSelector } from 'react-redux';

import { getIsDistanceCalculatingEnabled } from '../../../selectors/distanceCalculating';
import {
  getGeoCoordinates,
  getInfrastructureMapTypes,
  getMainPromoPin,
  getNewbuildingId,
  getPolygon,
} from '../../../selectors/newbuilding';
import { IInfrastructureItem } from '../../../types/map';
import { getGeoObject, getInfrastructureItems, getPolygonGeoObject } from '../helpers';

export const useGeoObjects = () => {
  const pinCoordinates = useSelector(getGeoCoordinates);
  const mainPromoPin = useSelector(getMainPromoPin);
  const newbuildingId = useSelector(getNewbuildingId);
  const newbuildingPolygon = useSelector(getPolygon);
  const isDistanceCalculatingEnabled = useSelector(getIsDistanceCalculatingEnabled);
  const infrastructureTypes = useSelector(getInfrastructureMapTypes);

  const infrastructureItemsRef = useRef(new Map<string, IInfrastructureItem>());

  const mapInfrastructure = useCallback((item: IInfrastructureItem) => {
    const { type, coordinates, id } = item;
    infrastructureItemsRef.current.set(id, item);

    return getGeoObject({ name: type, coordinates, id });
  }, []);

  const handleGeoObjectsFirstFetch = async (): Promise<IMapGeoObject[]> => {
    if (!infrastructureItemsRef.current.size) {
      const mainObjects = [];

      const main = getGeoObject({
        name: 'main',
        coordinates: pinCoordinates as IMapCoordinates,
        id: '#newbuilding',
        mainPromoPin,
      });

      mainObjects.push(main);

      if (newbuildingPolygon && isDistanceCalculatingEnabled) {
        const polygon = getPolygonGeoObject({
          name: 'main',
          coordinates: newbuildingPolygon,
          id: '#newbuilding-polygon',
        });

        mainObjects.push(polygon);
      }

      const infrastructure: IInfrastructureItem[] = infrastructureTypes
        ? await getInfrastructureItems(newbuildingId)
        : [];

      return Promise.resolve([...infrastructure.map(mapInfrastructure), ...mainObjects]);
    }

    return Promise.resolve([]);
  };

  return {
    infrastructureItemsRef,
    infrastructureTypes,
    onGeoObjectsFirstFetch: handleGeoObjectsFirstFetch,
  };
};
